/**
 * Dialoge der Rechnungsverwaltung: Zahlung buchen, Stornieren.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { recordPaymentRpc, cancelInvoice } from '@/lib/invoiceApi';

const ACCOUNTS = [
  { no: '1800', label: 'Bank (1800)' },
  { no: '1810', label: 'PayPal (1810)' },
  { no: '1600', label: 'Kasse (1600)' },
];

const parseAmount = (s) => Number(String(s).replace(/\./g, '').replace(',', '.'));

export function PaymentDialog({ row, open, onOpenChange, actor, onDone }) {
  const offen = Number(row?.openItem?.open_amount || 0);
  const [amount, setAmount] = useState(offen.toFixed(2).replace('.', ','));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState('1800');
  const [busy, setBusy] = useState(false);
  if (!row) return null;

  const value = parseAmount(amount);
  const valid = Number.isFinite(value) && value > 0 && value <= offen + 0.005;

  const save = async () => {
    setBusy(true);
    try {
      await recordPaymentRpc({ openItemId: row.openItem.id, amount: value, paymentDate: date, bankAccountNo: account, actorEmail: actor });
      toast.success(value < offen - 0.005 ? `Teilzahlung ${fmtEUR(value)} gebucht — Rest ${fmtEUR(offen - value)}.` : 'Zahlung gebucht — Rechnung bezahlt.');
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Zahlung buchen</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{row.number}</span> · {row.customer} · offen {fmtEUR(offen)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-medium mb-1">Betrag</span>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 tabular-nums" />
            {!valid && <span className="text-xs text-red-700">Betrag zwischen 0,01 und {fmtEUR(offen)}.</span>}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium mb-1">Zahlungsdatum</span>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1">Eingegangen auf</span>
              <select value={account} onChange={(e) => setAccount(e.target.value)} className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm">
                {ACCOUNTS.map((a) => <option key={a.no} value={a.no}>{a.label}</option>)}
              </select>
            </label>
          </div>
          <p className="text-xs text-slate-500">Für Bank- und PayPal-Umsätze besser über „Bank &amp; Zahlungen“ zuordnen — dann ist der Umsatz verknüpft.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={!valid || busy}>{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Buchen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StornoDialog({ row, open, onOpenChange, actor, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const paid = row.openItem ? Number(row.openItem.original_amount) - Number(row.openItem.open_amount) : 0;

  const save = async () => {
    setBusy(true);
    try {
      const res = await cancelInvoice(row.inv.id, reason.trim(), actor);
      toast.success(`Stornorechnung ${res.storno_no} erstellt${res.guthaben > 0 ? ` — ${fmtEUR(res.guthaben)} als Kundenguthaben` : ''}.`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechnung stornieren</DialogTitle>
          <DialogDescription>
            Es wird eine Stornorechnung (SR-…) mit Gegenbuchung erstellt. Die Rechnung <span className="font-mono">{row.number}</span> bleibt unverändert erhalten (GoBD).
          </DialogDescription>
        </DialogHeader>
        {paid > 0.004 && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            Bereits gezahlt: {fmtEUR(paid)} — wird als Kundenguthaben geführt (verrechnen oder erstatten).
          </p>
        )}
        <label className="block">
          <span className="block text-sm font-medium mb-1">Grund (Pflicht)</span>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="z. B. Bestellung vom Kunden storniert" />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={save} disabled={!reason.trim() || busy}>
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Stornorechnung erstellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
